import React, { useEffect, useRef, useState } from 'react';
import { GrMoreVertical } from "react-icons/gr";
import Pagination from "../../components/pagination/pagination";
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../components/button/button';
import { userDelete, userListAction, userStatus } from '../../redux/actions/userAction';
import errorToast from '../../components/toasts/errorToast';
import { useDispatch } from 'react-redux';
import successToast from '../../components/toasts/successToast';
import { formatString } from '../../components/helper';
import UploadUserModal from '../../components/modals/users/uploadUser';
import ReactSelectFiled from '../../components/forms/reactSelect';
import { useAuth } from '../../context/AuthContext';

export const Users = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAdmin, isTeamLead } = useAuth();

    const [filteredData, setFilteredData] = useState([]);
    const [nameFilter, setNameFilter] = useState(""); // For Name Filter
    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("")
    const [lastName, setLastName] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // For Status Filter
    const [emailFilter, setEmailFilter] = useState(""); // For Email Filter
    const [signInFilter, setSignInFilter] = useState(""); // For Sign-in Filter
    const [userType, setUserType] = useState("")
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    const [teamName, setTeamName] = useState("")
    const [loading, setLoading] = useState(false); // Don't load initially
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [paginationData, setPaginationData] = useState({});
    const [showOptions, setShowOptions] = useState(false);
    const [showClearIndex, setShowClearIndex] = useState(null);
    const [filtersApplied, setFiltersApplied] = useState(false); // Track filter state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dropdownRef = useRef(null);

    const onSuccess = (data) => {
        setPaginationData(data?.data?.data?.pagination);
        setFilteredData(data?.data?.data?.users);
        setLoading(false);
    };
    const onError = (data) => {
        errorToast(data?.data?.message);
        setLoading(false);
    };

    // Function to apply filters and fetch filtered data
    const handleApplyFilters = () => {
        setLoading(true);
        setFiltersApplied(true); // Show the table after filters are applied
        dispatch(userListAction({
            page,
            page_size: rowsPerPage,
            name: nameFilter,
            status: statusFilter,
            email: emailFilter,
            sign_in: signInFilter,
            user_type: userType,
            from_date: startDate,
            to_date: endDate,
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            team_name: teamName
        }, onSuccess, onError));
    };

    // Function to fetch all records without filters
    const handleGetAllRecords = () => {
        setLoading(true);
        setEmailFilter('');
        setUserType('');
        setNameFilter('');
        setSignInFilter('');
        setStatusFilter('')
        setStartDate('');
        setEndDate('');
        setTeamName("");
        setFiltersApplied(true); // Show the table
        dispatch(userListAction({ page, page_size: rowsPerPage }, onSuccess, onError)); // No filters
    };

    const handleClear = () => {
        setLoading(false);
        setEmailFilter('');
        setUserType('');
        setNameFilter('');
        setFirstName('');
        setLastName('');
        setMiddleName('');
        setSignInFilter('');
        setStatusFilter('')
        setStartDate('');
        setEndDate('');
        setTeamName('');
        setFiltersApplied(false);
    };


    useEffect(() => {
        if (filtersApplied) {
            setLoading(true);
            dispatch(userListAction({
                page,
                page_size: rowsPerPage,
                name: nameFilter,
                status: statusFilter,
                email: emailFilter,
                sign_in: signInFilter,
                user_type: userType,
                from_date: startDate,
                to_date: endDate,
                first_name: firstName,
                middle_name: middleName,
                last_name: lastName,
                team_name: teamName
            }, onSuccess, onError));
        }
    }, [page, rowsPerPage, nameFilter, statusFilter, emailFilter, signInFilter, filtersApplied, userType, startDate, endDate, firstName, middleName, lastName, teamName, dispatch]);

    const handleMoreOptionsClick = (item, index) => {
        setShowClearIndex(index);
        setShowOptions(!showOptions);
    };

    const handleEnableClick = (id) => {
        setLoading(true);
        dispatch(userStatus({ id }, onDeleteSuccess, onError));
    };

    const handleDeleteClick = (id) => {
        setLoading(true);
        dispatch(userDelete({ id }, onDeleteSuccess, onError));
    };

    const onDeleteSuccess = (data) => {
        dispatch(userListAction({
            page, page_size: rowsPerPage,
            name: nameFilter,
            status: statusFilter,
            email: emailFilter,
            sign_in: signInFilter,
            user_type: userType,
            from_date: startDate,
            to_date: endDate,
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            team_name: teamName
        }, onSuccess, onError));
        setShowOptions(false);
        setShowClearIndex(null);
        successToast(data?.data?.message);
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowOptions(false);
            setShowClearIndex(null);
        }
    };

    if (!isAdmin || isTeamLead) {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <i className="bi bi-shield-lock fs_40 text-danger mb-3"></i>
                <h5 className="fw_600">Access Denied</h5>
                <p className="text-muted fs_14">This page is only accessible to company administrators and team leads.</p>
            </div>
        );
    }

    return (
        <div className="d-flex vh-100 overflow-auto" onClick={handleClickOutside}>
            <div className="w-100">
                <div className="w-100 header align-items-center border-bottom  d-flex position-sticky top-0 bg-white py-2 justify-content-between z-1">
                    <h2 className="fs_20 fw_400 fn_Roboto text-start px-3 pt-1">Users</h2>
                    {isAdmin &&
                        <div className='d-flex gap-3 px-3'>
                            <CustomButton className='cursor_pointer fs_18 pb-1 d-flex align-items-center justify-content-center text-nowrap' style={{ height: '35px', width: '110px' }} variant='primary' onClick={() => setIsModalOpen(true)}>Import Users</CustomButton>
                            <CustomButton className='cursor_pointer fs_18 pb-1 d-flex align-items-center justify-content-center' style={{ height: '35px', width: '110px' }} variant='primary' onClick={() => navigate('/add-user')}>Add User +</CustomButton>
                        </div>
                    }
                </div>



                <div className='d-flex flex-wrap gap-3 p-3'>
                    <div className='text-start '>
                        <span className="">First Name</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='text'
                                name='name'
                                className='input-field w-100'
                                placeholder="Enter first name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                }}
                            />
                        </div>
                    </div>

                    <div className='text-start '>
                        <span className="">Middle Name</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='text'
                                name='name'
                                className='input-field w-100'
                                placeholder="Enter middle name"
                                value={middleName}
                                onChange={(e) => setMiddleName(e.target.value)}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                }}
                            />
                        </div>
                    </div>

                    <div className='text-start '>
                        <span className="">Last Name</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='text'
                                name='name'
                                className='input-field w-100'
                                placeholder="Enter last name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                }}
                            />
                        </div>
                    </div>

                    <div className='text-start '>
                        <span className="">Email</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='email'
                                name='email'
                                className='input-field w-100'
                                placeholder="Enter email"
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                            />
                        </div>
                    </div>

                    {isAdmin &&
                        <div className='text-start '>
                            <span className="">Team Name</span>
                            <div className='' style={{ width: '14rem' }}>
                                <input
                                    type='text'
                                    name='team_name'
                                    className='input-field w-100'
                                    placeholder="Enter team name"
                                    value={teamName}
                                    onChange={(e) => setTeamName(e.target.value)}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                    }}
                                />
                            </div>
                        </div>
                    }

                    <div className='text-start ' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => setSignInFilter(value)}
                            options={[
                                {
                                    label: "Google",
                                    value: "google"
                                },
                                {
                                    label: "Email",
                                    value: "email"
                                }
                            ]}
                            label={"Sign In Method"}
                            value={signInFilter}
                            placeholder={"Select sign-in option"}
                        />
                    </div>

                    <div className='text-start ' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => setUserType(value)}
                            options={[
                                {
                                    label: "Admin",
                                    value: "admin"
                                },
                                {
                                    label: "User",
                                    value: "user"
                                },
                                {
                                    label: "Team Lead",
                                    value: "team_lead"
                                }
                            ]}
                            label={"User Type"}
                            value={userType}
                        />
                    </div>

                    <div className='text-start ' style={{ width: '14rem' }}>
                        <ReactSelectFiled
                            onChange={(value) => setStatusFilter(value)}
                            options={[
                                {
                                    label: "Active",
                                    value: "active"
                                },
                                {
                                    label: "Inactive",
                                    value: "inactive"
                                }
                            ]}
                            label={"Status"}
                            value={statusFilter}
                        />
                    </div>

                    <div className='text-start '>
                        <span className="">From Date</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='date'
                                name='startDate'
                                className='input-field w-100'
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>
                    <div className='text-start '>
                        <span className="">To Date</span>
                        <div className='' style={{ width: '14rem' }}>
                            <input
                                type='date'
                                name='endDate'
                                className='input-field w-100'
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>

                </div>


                <div className="d-flex flex-wrap justify-content-start gap-3 mx-3 pt-1 text-nowrap">
                    <CustomButton variant="primary" onClick={handleApplyFilters} disabled={!firstName && !middleName && !lastName && !signInFilter && !userType && !nameFilter && !statusFilter && !emailFilter && !startDate && !endDate} >
                        Apply Filters
                    </CustomButton>
                    <CustomButton variant="primary" onClick={handleGetAllRecords}>
                        Show All
                    </CustomButton>
                    <CustomButton variant="secondary" onClick={handleClear}>
                        Clear Filters
                    </CustomButton>
                </div>

                {filtersApplied && (
                    <>
                        <div className="m-3">
                            <div className="table-responsive overflow-auto " style={{ minHeight: '18rem' }}>
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr className="text-start text-nowrap ">
                                            <th width={'8%'} className='fw_400'>Sl No.</th>
                                            <th width={'18%'} className='fw_400'>Full Name</th>
                                            <th width={'18%'} className='fw_400'>Email (Login ID)</th>
                                            <th width={'12%'} className='fw_400'>Teams</th>
                                            <th width={'12%'} className='fw_400'>Sign-in Method</th>
                                            <th width={'18%'} className='fw_400'>User Type</th>
                                            <th width={'12%'} className='fw_400'>Status</th>
                                            <th width={'20%'} className='fw_400'>Create Date</th>
                                            {isAdmin && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9">Loading...</td>
                                            </tr>
                                        ) : filteredData?.length > 0 ? (
                                            filteredData?.map((item, index) => (
                                                <tr key={index} className='text-start'>
                                                    <td>{index + 1}</td>
                                                    <td width={'5%'}>{formatString(item?.salutation)}. {formatString(item?.first_name)} {formatString(item?.middle_name)} {formatString(item?.last_name)}</td>
                                                    <td>{item?.email}</td>
                                                    <td>
                                                        {item?.teams?.map((team) => team.name).join(', ')}
                                                        {item?.teamUsers?.map((teamUser) => teamUser.team?.name).length > 0 &&
                                                            `${item?.teamUsers?.map((teamUser) => teamUser.team?.name).join(', ')}`}
                                                    </td>
                                                    <td>{formatString(item?.sign_in)}</td>
                                                    <td>{formatString(item?.user_type)}</td>
                                                    <td>{item?.status === true ? 'Active' : 'Inactive'}</td>
                                                    <td>{moment(item?.createdAt).format('MM/DD/YY, hh:mm A')}</td>
                                                    {isAdmin &&
                                                        <td>
                                                            <GrMoreVertical
                                                                className="cursor_pointer"
                                                                onClick={() => handleMoreOptionsClick(item, index)}
                                                            />
                                                            {showOptions && showClearIndex === index && (
                                                                <div className="position-relative" ref={dropdownRef}>
                                                                    <div className="position-absolute" style={{ left: "-5rem" }}>
                                                                        <div className="card" style={{ width: '5rem' }}>
                                                                            <div className="card-body p-2">
                                                                                <label className="d-block cursor_pointer hover-effect" onClick={() => { handleEnableClick(item.id) }}>{item.status ? "Disable" : "Enable"}</label>
                                                                                {/* <label className="d-block text-danger cursor_pointer hover-effect" onClick={() => { handleDeleteClick(item.id) }}>Delete</label> */}
                                                                                <label className="d-block cursor_pointer hover-effect" onClick={() => { navigate(`/view-user/${item.id}`) }}>View</label>
                                                                                <label className="d-block cursor_pointer hover-effect" onClick={() => { navigate(`/edit-user/${item.id}`) }}>Edit</label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    }
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="mx-4 pb-3 d-flex justify-content-end">
                            {!loading &&
                                <Pagination
                                    data={paginationData}
                                    nextFunc={() => setPage(page + 1)}
                                    prevFunc={() => setPage(page - 1)}
                                    setPageRowFunc={(value) => {
                                        setPage(1);
                                        setRowsPerPage(value);
                                    }}
                                />
                            }
                        </div>
                    </>
                )}
            </div>
            <UploadUserModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};
